// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Pack selector vocabulary (the ONLY selectors src may use) ──
/**
 * The `register_surface` an installed pack form declares to feed this
 * register's purpose vocabulary. Generic on purpose: any instrument that makes
 * consent load-bearing for a named processing purpose can declare it.
 */
export const CREDIT_INFORMATION_USE_REGISTER_SURFACE = 'credit_information_use';
/** The form field id whose `options[]` the purpose vocabulary reader collects. */
export const CREDIT_INFORMATION_USE_VOCABULARY_FIELDS = {
    purposes: 'credit_information_purpose',
};
/**
 * The boundary tag this register reads: guidance an installed framework
 * authors under this tag is what the register renders as its boundary
 * statement, through `CreditInformationService.boundaryStatements`.
 *
 * Scoped by the register's OWN declaring records — the frameworks whose
 * installed form templates carry {@link CREDIT_INFORMATION_USE_REGISTER_SURFACE}
 * with purpose options — and not by a deadline `event_type`.
 *
 * ## fix-sprint-20260922-2142 F13
 *
 * This file used to export `CREDIT_INFORMATION_USE_EVENT_TYPE =
 * 'sector_compliance_assessment_cycle'`, documented as the deadline event type
 * that makes this register applicable, and this tag, documented as the one the
 * register reads. Neither had a reader, and no pack authored the tag, so the
 * page rendered product copy where the instrument's statement belonged. The
 * event-type constant was deleted rather than wired: the deadline rows that
 * carry that event type declare no `request_types`, so the deadline-based
 * discovery (`installedDeclaringFrameworkCodes`) returns nothing for them and a
 * reader scoped through it could never render a statement — and the event type
 * is shared by three unrelated annual-assessment cycles besides. The tag is now
 * read, and `vn-pdpl` authors a statement under it.
 */
export const CREDIT_INFORMATION_USE_BOUNDARY_TAG = 'credit_information_use_boundary';
// ── The record ──
/**
 * Why a use could not be recorded.
 *
 * Two distinct failures, kept apart because they tell an operator to do
 * different things: `no_consent_found` means nothing was named or nothing
 * resolved, and `consent_does_not_cover_purpose` means a real consent exists
 * and did not name this purpose (Decree 356 Điều 8(2)(a)). Collapsing them into
 * one "not permitted" would leave the operator unable to tell "get consent"
 * from "the consent you have is the wrong one".
 */
export const CREDIT_INFORMATION_REFUSAL_REASONS = [
    'no_consent_found',
    'consent_does_not_cover_purpose',
    'consent_withdrawn',
];
/**
 * The record's own account of its state.
 *
 * Not invented vocabulary and not a single-valued enum: a consent can be
 * WITHDRAWN after the use was recorded. Decree 356/2025/NĐ-CP Điều 8(2)(đ)
 * requires the organisation to state how consent may be withdrawn, so a
 * withdrawal is a thing the instrument expects to happen — and a register that
 * could not show it would tell a later reader that the basis still holds when
 * it does not.
 *
 * What it is NOT: a verdict. `consent_withdrawn` says the basis the use rested
 * on has since gone, which is a fact. Whether that makes the past use unlawful
 * is a legal position and the platform takes none.
 */
export const CREDIT_INFORMATION_USE_STATES = ['consent_in_force', 'consent_withdrawn'];
//# sourceMappingURL=credit-information.js.map